"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";

/**
 * The animated orb beside "Thinking…" (owner, 2026-08-26).
 *
 * Purely decorative: the indicator it sits in is already `aria-hidden`, and the
 * thread's own `role="log"` carries the state to assistive tech, so this adds
 * no accessible content.
 *
 * DEGRADES TO NOTHING, deliberately, in three cases — WebGPU absent (Firefox
 * and older Safari at time of writing, plus most of the Android fleet), the
 * visitor asking for reduced motion, or any runtime failure. In all three the
 * indicator renders exactly as it did before this component existed: the
 * shimmering text alone. A placeholder dot was considered and dropped — a
 * static dot beside "Thinking…" reads as a broken animation rather than as a
 * design.
 *
 * Defaults to the WHITE orb (owner's call, 2026-08-26): it reads against the
 * chat's light background, where the dark one sank into it. "dark" is kept in
 * LIQUID_ORB_VARIANTS as the exported original and as a one-word rollback;
 * compare them at /chat/preview/orb.
 */
export default function LiquidOrb({ size = 26, variant = "white" }: { size?: number; variant?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const reducedMotion = useReducedMotion();
  const [failed, setFailed] = useState(false);
  // `navigator.gpu` cannot be read during render (it differs between the server
  // and the client, which is a hydration mismatch), so support is resolved in
  // the effect below and the canvas is withheld until then.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (reducedMotion || failed) return;
    if (typeof navigator === "undefined" || !navigator.gpu) { setFailed(true); return; }
    setReady(true);
  }, [reducedMotion, failed]);

  useEffect(() => {
    // Gated on the SAME condition as the render below, and depending on the
    // same values — otherwise flipping reduced-motion mid-conversation removes
    // the canvas (the render returns null) while this effect's deps are
    // unchanged, so its cleanup never runs and the rAF loop keeps drawing into
    // a detached canvas for the life of the component. Found in review
    // 2026-08-26; it also made the reduced-motion promise in the doc above a
    // lie for anyone who flipped the setting after the orb started.
    if (!ready || reducedMotion || failed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let alive = true;
    let stop: (() => void) | undefined;
    // The renderer AND the shader are behind this import, so a browser without
    // WebGPU never downloads either.
    void import("./liquid-orb-gpu")
      .then((m) => m.startOrb(canvas, size, variant))
      .then((s) => { if (alive) { stop = s; } else { s(); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; stop?.(); };
  }, [ready, reducedMotion, failed, size, variant]);

  if (reducedMotion || failed || !ready) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="shrink-0"
      style={{ width: size, height: size }}
    />
  );
}
